import {
  BrandingThemeAddSubmission,
  BrandingThemeItem,
} from '@/types/brandingThemes';
import { Stack } from '@mui/material';
import { useForm } from 'react-hook-form';
import ThemeNameForm from './ThemeNameForm';
import WebsiteAndEmailsForm from './WebsiteAndEmailsForm';
import WebsiteDemo from '../demos/WebsiteDemo';
import MobileUIForm from './MobileUIForm';
import { SVForm } from '@/components/core';
import { ROUTES } from '@/constants/routes';
import { useNavigate } from 'react-router-dom';
import {
  useAddBrandingTheme,
  useEditBrandingTheme,
} from '@/hooks/requests/useBrandingThemes';
import showToast from '@/utils/toast';
import { MESSAGES } from '@/constants/messages';
import FormSubmissionButtons from '../../FormSubmissionButtons';

type BrandingThemeFormProps = {
  defaultValues?: BrandingThemeItem;
};

export default function BrandingThemeForm({
  defaultValues,
}: BrandingThemeFormProps) {
  const navigate = useNavigate();
  const formMethods = useForm<BrandingThemeAddSubmission>({
    defaultValues: defaultValues
      ? defaultValues
      : {
          name: '',
          primaryColor: '',
          secondaryColor: '',
          buttonFontColor: '',
          logoUrl: '',
          mobileUi: {
            style: 'Custom',
            customHeroBackground: 'Silver Metal',
            customNavigationBackground: 'Silver Metal',
            customHomeScreenBackground: 'Silver Metal',
            customBottomNavigationSelectedIconColor: '#000000',
            primaryColor: '#1B478A',
            secondaryColor: '#6D6E71',
            titleColor: '#000000',
            titleHeroSectionColor: '#FFFFFF',
            titleAccentSectionColor: '#000000',
            mobileLogoUrl: '',
            introVideo: 'America',
            splashScreen: '1 Advisor',
            font: 'Roboto',
          },
        },
    mode: 'onBlur',
  });
  const { addBrandingTheme, isAddBrandingThemePending } = useAddBrandingTheme();
  const { editBrandingTheme, isEditBrandingThemePending } =
    useEditBrandingTheme();

  const [primaryColor, secondaryColor, buttonFontColor, logoUrl] =
    formMethods.watch([
      'primaryColor',
      'secondaryColor',
      'buttonFontColor',
      'logoUrl',
    ]);

  async function onSubmit(data: BrandingThemeAddSubmission) {
    try {
      if (defaultValues) {
        await editBrandingTheme({
          ...data,
          uuid: defaultValues.uuid,
          isProgramDefault: defaultValues.isProgramDefault,
        });
        showToast({
          type: 'success',
          data: { content: 'Your branding theme has been successfully edited' },
        });
      } else {
        await addBrandingTheme({ ...data, isProgramDefault: false });
        showToast({
          type: 'success',
          data: { content: 'Your branding theme has been successfully added' },
        });
      }
      navigate(ROUTES.BRANDING_THEMES);
    } catch {
      showToast({ type: 'error', data: { content: MESSAGES.genericError } });
    }
  }

  return (
    <SVForm<BrandingThemeAddSubmission>
      methods={formMethods}
      onSubmit={onSubmit}>
      <Stack spacing={2}>
        <ThemeNameForm />
        <WebsiteAndEmailsForm
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          buttonFontColor={buttonFontColor}
          logoUrl={logoUrl}
        />
        {primaryColor && secondaryColor && (
          <WebsiteDemo
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
            logoUrl={logoUrl}
          />
        )}
        <MobileUIForm />
        <FormSubmissionButtons
          isLoading={isAddBrandingThemePending || isEditBrandingThemePending}
          onLeave={() => navigate(ROUTES.BRANDING_THEMES)}
        />
      </Stack>
    </SVForm>
  );
}
